import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Alert, Badge, Button, ButtonGroup, Card, Col, Container, Form, Row, Spinner, Navbar, ProgressBar } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import OnboardingUploadModal from '../components/OnboardingUploadModal';
import OnboardingFormModal from '../components/OnboardingFormModal';
import SignatureField from '../components/SignatureField';
import { formHasAllRequiredSignatures, normalizeFormFields } from '../utils/signatureFieldUtils';
import { useDocumentRequirement } from '../contexts/DocumentRequirementContext';
import RichTextEditor from '../components/RichTextEditor';

/** Synthetic package assignment id for director/admin “what residents see” preview (no API rows). */
export const ONBOARDING_ADMIN_PREVIEW_PACKAGE_ASSIGNMENT_ID = -1000000001;

function buildAdminPreviewSectionAssignments(preview) {
  if (!preview || !Array.isArray(preview.sections)) return [];
  const pkgAssignId = ONBOARDING_ADMIN_PREVIEW_PACKAGE_ASSIGNMENT_ID;
  return preview.sections.map((sec) => ({
    id: `preview-sa-${sec.id}`,
    status: 'assigned',
    dueDate: null,
    section: {
      id: sec.id,
      title: sec.title,
      instructions: sec.instructions,
      items: (sec.items || []).map((it) => ({ ...it }))
    },
    itemResponses: [],
    packageAssignment: {
      id: pkgAssignId,
      customName: preview.packageName,
      package: {
        name: preview.packageName,
        description: preview.packageDescription || ''
      }
    }
  }));
}

/** Secure uploads are served with Bearer auth; plain <a href> navigations omit the header and get 401. */
async function openSecureOnboardingFileInNewTab(webPath) {
  if (!webPath) return;
  const token = localStorage.getItem('token');
  if (!token) {
    window.alert('You are not signed in.');
    return;
  }
  const pathPart = webPath.startsWith('/') ? webPath : `/${webPath}`;
  const url = `${API_ENDPOINTS.PROFILE_PICS}${pathPart}`;
  try {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) {
      let msg = 'Could not open file.';
      try {
        const j = await r.json();
        if (j?.error) msg = j.error;
      } catch {
        /* ignore */
      }
      window.alert(msg);
      return;
    }
    const blob = await r.blob();
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120000);
  } catch (e) {
    console.error(e);
    window.alert('Could not open file. Please try again.');
  }
}

const DocumentCompletionPageV2 = ({
  packageAssignmentId = null,
  hideNavbar = false,
  embedded = false,
  showContinueToApp = true,
  adminPreview = null,
  /** Called whenever section assignments are loaded or refreshed (resident combined completion page). */
  onSectionAssignmentsChange = null,
  /** When true, hide director-approved sections and omit empty package groups (e.g. embedded DocumentCompletionPage). */
  hideApprovedSections = false,
  /** Report progress for sections that are not director-approved (percent 0–100; percent null if no section assignments). */
  onPendingSectionsProgress = null
}) => {
  const navigate = useNavigate();
  const { setIsCompleted } = useDocumentRequirement();

  const isAdminPreview = Boolean(adminPreview);

  const [loading, setLoading] = useState(() => !adminPreview);
  const [error, setError] = useState('');

  const [sectionAssignments, setSectionAssignments] = useState(() =>
    adminPreview ? buildAdminPreviewSectionAssignments(adminPreview) : []
  );
  const [standaloneFormAssignments, setStandaloneFormAssignments] = useState([]);
  const [myFormSubmissions, setMyFormSubmissions] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null); // { sectionAssignmentId, itemId, requiredFileTypes }
  const [activeInfoFormTarget, setActiveInfoFormTarget] = useState(null); // { sectionAssignmentId, item, readOnly, reviewNotes }
  const [locallySubmittedSections, setLocallySubmittedSections] = useState(() => new Set());

  const [infoFormValuesByItemId, setInfoFormValuesByItemId] = useState({});
  const [signingSignatureKey, setSigningSignatureKey] = useState(null);

  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');

  const headers = useMemo(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const fetchMySectionAssignments = async () => {
    try {
      setPackagesLoading(true);
      setError('');
      if (!token) throw new Error('Not authenticated');

      const [sectionsRes, assignmentsRes, formSubsRes] = await Promise.all([
        axios.get(`${API_ENDPOINTS.ONBOARDING}/package-section-assignments/me`, { headers }),
        axios.get(`${API_ENDPOINTS.ONBOARDING}/assignments/me`, { headers }),
        axios.get(`${API_ENDPOINTS.ONBOARDING}/form-submissions/me`, { headers })
      ]);
      const list = Array.isArray(sectionsRes.data) ? sectionsRes.data : [];
      const allAssignments = Array.isArray(assignmentsRes.data) ? assignmentsRes.data : [];
      const formAssignments = allAssignments.filter((a) =>
        Boolean(a?.formId || a?.form) && !a?.packageId && !a?.package
      );
      const formSubs = Array.isArray(formSubsRes.data) ? formSubsRes.data : [];
      setSectionAssignments(list);
      setStandaloneFormAssignments(formAssignments);
      setMyFormSubmissions(formSubs);
      onSectionAssignmentsChange?.(list);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e.message || 'Failed to load section assignments');
      setSectionAssignments([]);
      setStandaloneFormAssignments([]);
      setMyFormSubmissions([]);
      onSectionAssignmentsChange?.([]);
    } finally {
      setPackagesLoading(false);
    }
  };

  useEffect(() => {
    if (adminPreview) {
      setLoading(false);
      setError('');
      setPackagesLoading(false);
      setSectionAssignments(buildAdminPreviewSectionAssignments(adminPreview));
      return;
    }
    fetchMySectionAssignments().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminPreview]);

  // Initialize local info form state based on current responses.
  useEffect(() => {
    const next = {};
    for (const sa of sectionAssignments) {
      for (const item of sa?.section?.items || []) {
        const resp = (sa?.itemResponses || []).find(r => r.itemId === item.id);
        next[item.id] = resp?.formData || {};
      }
    }
    setInfoFormValuesByItemId(next);
  }, [sectionAssignments]);

  const assignmentsForDisplay = useMemo(() => {
    if (!hideApprovedSections || isAdminPreview) return sectionAssignments;
    return sectionAssignments.filter((sa) => sa.status !== 'approved');
  }, [sectionAssignments, hideApprovedSections, isAdminPreview]);

  const groupedByPackageAssignment = useMemo(() => {
    const groups = {};
    for (const sa of assignmentsForDisplay) {
      const pkgAssignId = sa?.packageAssignment?.id;
      if (!pkgAssignId) continue;
      if (!groups[pkgAssignId]) {
        groups[pkgAssignId] = {
          packageAssignment: sa.packageAssignment,
          sectionAssignments: []
        };
      }
      groups[pkgAssignId].sectionAssignments.push(sa);
    }
    const arr = Object.values(groups)
      .filter((g) => (g.sectionAssignments || []).length > 0)
      .sort((a, b) => (b.packageAssignment?.createdAt || 0) - (a.packageAssignment?.createdAt || 0));
    if (packageAssignmentId) return arr.filter(g => g.packageAssignment?.id === packageAssignmentId);
    return arr;
  }, [assignmentsForDisplay, packageAssignmentId]);

  const isAllSectionsApproved = useMemo(() => {
    if (!sectionAssignments.length && !standaloneFormAssignments.length) return false;
    const sectionsApproved = sectionAssignments.length > 0
      ? sectionAssignments.every(sa => sa.status === 'approved')
      : true;
    const formsSubmittedOrApproved = standaloneFormAssignments.every((assignment) =>
      myFormSubmissions.some((submission) =>
        Number(submission.assignmentId) === Number(assignment.id) &&
        (submission.status === 'submitted' || submission.status === 'approved')
      )
    );
    return sectionsApproved && formsSubmittedOrApproved;
  }, [sectionAssignments, standaloneFormAssignments, myFormSubmissions]);

  const progressPercentage = useMemo(() => {
    const totalItems = sectionAssignments.reduce((acc, sa) => acc + (sa.section?.items?.length || 0), 0);
    const completedItems = sectionAssignments.reduce(
      (acc, sa) => acc + (sa.section?.items?.filter(item => itemIsCompleteByUser(sa, item)).length || 0),
      0
    );
    const totalFormAssignments = standaloneFormAssignments.length;
    const completedFormAssignments = standaloneFormAssignments.filter((assignment) =>
      myFormSubmissions.some((submission) =>
        Number(submission.assignmentId) === Number(assignment.id) &&
        (submission.status === 'submitted' || submission.status === 'approved')
      )
    ).length;
    const total = totalItems + totalFormAssignments;
    const completed = completedItems + completedFormAssignments;
    if (!total) return 0;
    return Math.round((completed / total) * 100);
  }, [sectionAssignments, standaloneFormAssignments, myFormSubmissions]);

  function getItemResponse(sa, itemId) {
    return (sa?.itemResponses || []).find(r => r.itemId === itemId) || null;
  }

  function itemIsCompleteByUser(sa, item) {
    const r = getItemResponse(sa, item.id);
    if (!r) return false;
    if (r.status === 'approved') return true;
    if (r.status === 'rejected') return false;

    if (r.status !== 'draft' && r.status !== 'submitted') return false;

    if (item.itemType === 'info_form') {
      const formData = r.formData;
      if (!formData || typeof formData !== 'object' || Object.keys(formData).length === 0) return false;
      const fields = normalizeFormFields(item.formSchema);
      return formHasAllRequiredSignatures(fields, formData);
    }

    if (item.itemType === 'file_upload') {
      const schema = item.formSchema || {};
      const isReferenceFiles = schema.contentMode === 'reference_files' && Array.isArray(schema.referenceFiles);
      if (!isReferenceFiles) {
        return Boolean(r.filePath || r.fileName || r.webPath);
      }
      const acknowledged = r.formData?.acknowledged || {};
      const uploadedFor = r.formData?.uploadedFor || {};
      return schema.referenceFiles.every((rf) => {
        if (!rf?.id) return false;
        return rf.completionMode === 'upload_required'
          ? Boolean(uploadedFor[rf.id])
          : Boolean(acknowledged[rf.id]);
      });
    }

    return false;
  }

  const canSubmitSection = (sa) => {
    if (!sa?.section?.items?.length) return false;
    if (sa.status === 'approved') return false;

    return sa.section.items.every(item => {
      const r = getItemResponse(sa, item.id);
      if (!r) return false;
      if (r.status === 'approved') return true;
      if (r.status === 'rejected') return false;
      if (r.status !== 'draft' && r.status !== 'submitted') return false;

      if (item.itemType === 'file_upload') {
        const schema = item.formSchema || {};
        const isReferenceFiles = schema.contentMode === 'reference_files' && Array.isArray(schema.referenceFiles);
        if (!isReferenceFiles) return Boolean(r.filePath || r.fileName || r.webPath);
        const acknowledged = r.formData?.acknowledged || {};
        const uploadedFor = r.formData?.uploadedFor || {};
        return schema.referenceFiles.every((rf) => {
          if (!rf?.id) return false;
          return rf.completionMode === 'upload_required'
            ? Boolean(uploadedFor[rf.id])
            : Boolean(acknowledged[rf.id]);
        });
      }
      if (item.itemType === 'info_form') {
        const formData = r.formData;
        if (!formData || typeof formData !== 'object' || Object.keys(formData).length === 0) return false;
        const fields = normalizeFormFields(item.formSchema);
        return formHasAllRequiredSignatures(fields, formData);
      }
      return false;
    });
  };

  /** Progress for sections still awaiting director approval (used by DocumentCompletionPage top bar). */
  const pendingSectionsProgressReport = useMemo(() => {
    if (isAdminPreview) {
      return { percent: null, totalSections: 0, pendingCount: 0 };
    }
    if (!sectionAssignments.length) {
      return { percent: null, totalSections: 0, pendingCount: 0 };
    }
    const pending = sectionAssignments.filter((sa) => sa.status !== 'approved');
    const totalSections = sectionAssignments.length;
    if (pending.length === 0) {
      return { percent: 100, totalSections, pendingCount: 0 };
    }
    let sum = 0;
    for (const sa of pending) {
      if (sa.status === 'submitted') {
        sum += 1;
        continue;
      }
      const items = sa.section?.items || [];
      if (!items.length) continue;
      let done = 0;
      for (const it of items) {
        if (itemIsCompleteByUser(sa, it)) done++;
      }
      sum += done / items.length;
    }
    return {
      percent: Math.round((sum / pending.length) * 100),
      totalSections,
      pendingCount: pending.length
    };
  }, [sectionAssignments, isAdminPreview]);

  useEffect(() => {
    if (!onPendingSectionsProgress) return;
    onPendingSectionsProgress(pendingSectionsProgressReport);
  }, [pendingSectionsProgressReport, onPendingSectionsProgress]);

  const submitSection = async (sectionAssignmentId) => {
    try {
      setError('');
      setLocallySubmittedSections((prev) => new Set(prev).add(sectionAssignmentId));
      setPackagesLoading(true);
      await axios.post(
        `${API_ENDPOINTS.ONBOARDING}/package-section-assignments/${sectionAssignmentId}/submit`,
        {},
        { headers }
      );
      await fetchMySectionAssignments();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e.message || 'Failed to submit section');
      setLocallySubmittedSections((prev) => {
        const next = new Set(prev);
        next.delete(sectionAssignmentId);
        return next;
      });
    } finally {
      setPackagesLoading(false);
    }
  };

  const handleItemUpload = async (filesWithCategories) => {
    if (!uploadTarget) return;
    const file = filesWithCategories?.[0]?.file;
    if (!file) return;

    try {
      setUploading(true);
      setError('');
      const fd = new FormData();
      fd.append('file', file);
      if (uploadTarget.referenceFileId) {
        fd.append('referenceFileId', String(uploadTarget.referenceFileId));
      }

      await axios.post(
        `${API_ENDPOINTS.ONBOARDING}/package-section-assignments/${uploadTarget.sectionAssignmentId}/items/${uploadTarget.itemId}/upload`,
        fd,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setShowUploadModal(false);
      setUploadTarget(null);
      await fetchMySectionAssignments();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const saveReferenceAcknowledgement = async (sectionAssignmentId, itemId, referenceFileId, checked) => {
    try {
      setPackagesLoading(true);
      setError('');
      const sa = sectionAssignments.find(x => x.id === sectionAssignmentId);
      const resp = (sa?.itemResponses || []).find(r => r.itemId === itemId);
      const current = resp?.formData && typeof resp.formData === 'object' ? resp.formData : {};
      const acknowledged = current.acknowledged && typeof current.acknowledged === 'object' ? { ...current.acknowledged } : {};
      acknowledged[String(referenceFileId)] = Boolean(checked);

      await axios.post(
        `${API_ENDPOINTS.ONBOARDING}/package-section-assignments/${sectionAssignmentId}/items/${itemId}/save-info`,
        { formData: { ...current, acknowledged } },
        { headers }
      );
      await fetchMySectionAssignments();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e.message || 'Failed to save acknowledgement');
    } finally {
      setPackagesLoading(false);
    }
  };

  const clearUploadedEvidence = async (sectionAssignmentId, itemId, referenceFileId = null) => {
    try {
      setPackagesLoading(true);
      setError('');
      await axios.post(
        `${API_ENDPOINTS.ONBOARDING}/package-section-assignments/${sectionAssignmentId}/items/${itemId}/clear-file`,
        referenceFileId ? { referenceFileId } : {},
        { headers }
      );
      await fetchMySectionAssignments();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e.message || 'Failed to remove uploaded file');
    } finally {
      setPackagesLoading(false);
    }
  };

  const saveInfoFormItemData = async (sectionAssignmentId, itemId, formData) => {
    try {
      setError('');
      setPackagesLoading(true);
      await axios.post(
        `${API_ENDPOINTS.ONBOARDING}/package-section-assignments/${sectionAssignmentId}/items/${itemId}/save-info`,
        { formData },
        { headers: { ...headers } }
      );
      await fetchMySectionAssignments();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e.message || 'Failed to save form');
    } finally {
      setPackagesLoading(false);
    }
  };
  const saveInfoFormItem = async (sectionAssignmentId, itemId) => {
    const formData = infoFormValuesByItemId[itemId] || {};
    await saveInfoFormItemData(sectionAssignmentId, itemId, formData);
  };

  const handleContinueToApp = async () => {
    try {
      await axios.put(
        `${API_ENDPOINTS.AUTH}/me`,
        { documentRequirementsCompleted: true },
        { headers }
      );
      setIsCompleted(true);
    } catch (e) {
      // Even if update fails, still navigate for UX.
      console.error(e);
    }
    const dashboardPath = role === 'director' ? '/dashboard' : `/${role}/dashboard`;
    navigate(dashboardPath);
  };

  const renderInfoForm = (item, sectionAssignmentId, readOnly = false) => {
    const schema = item?.formSchema || {};
    const fields = Array.isArray(schema.fields) ? schema.fields : [];

    if (!fields.length) {
      return <Alert variant="secondary" className="mt-2">Info form schema not configured.</Alert>;
    }

    const values = infoFormValuesByItemId[item.id] || {};
    const formatPhoneValue = (input) => {
      const digits = String(input || '').replace(/\D/g, '').slice(0, 10);
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    };
    const formatSsnValue = (input) => {
      const digits = String(input || '').replace(/\D/g, '').slice(0, 9);
      if (digits.length <= 3) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    };
    const maskSsnValue = (input) => {
      const formatted = formatSsnValue(input);
      if (!formatted) return '';
      const digits = formatted.replace(/\D/g, '');
      const last4 = digits.slice(-4);
      return last4 ? `***-**-${last4}` : '***-**-****';
    };
    const getContainerEntryKey = (container) => `__container_entries__${container?.id || container?.key || 'container'}`;
    const resolveFieldKeyForEntry = (baseFieldKey, entryId) => (entryId === 'legacy' ? baseFieldKey : `${baseFieldKey}__entry_${entryId}`);
    const getContainerEntryIds = (container) => {
      if (!container?.allowMultipleEntries) return ['legacy'];
      const raw = values[getContainerEntryKey(container)];
      if (Array.isArray(raw) && raw.length > 0) return raw;
      return ['legacy'];
    };
    const evaluateConditionalRule = (rule, entryId = 'legacy') => {
      if (!rule?.sourceFieldId) return true;
      const sourceKey = entryId === 'legacy' ? rule.sourceFieldId : `${rule.sourceFieldId}__entry_${entryId}`;
      return values?.[sourceKey] === true;
    };
    const addContainerEntry = (container) => {
      if (readOnly || !container?.allowMultipleEntries) return;
      const nextId = `entry_${Date.now()}_${Math.round(Math.random() * 1e4)}`;
      const key = getContainerEntryKey(container);
      setInfoFormValuesByItemId((prev) => {
        const currentItem = prev[item.id] || {};
        const currentEntries = Array.isArray(currentItem[key]) && currentItem[key].length > 0
          ? currentItem[key]
          : ['legacy'];
        return {
          ...prev,
          [item.id]: {
            ...currentItem,
            [key]: [...currentEntries, nextId]
          }
        };
      });
    };
    const removeContainerEntry = (container, entryId) => {
      if (readOnly || !container?.allowMultipleEntries || entryId === 'legacy') return;
      const key = getContainerEntryKey(container);
      const entryToken = `__entry_${entryId}`;
      setInfoFormValuesByItemId((prev) => {
        const currentItem = { ...(prev[item.id] || {}) };
        const currentEntries = Array.isArray(currentItem[key]) && currentItem[key].length > 0
          ? currentItem[key]
          : ['legacy'];
        currentItem[key] = currentEntries.filter((id) => id !== entryId);
        Object.keys(currentItem).forEach((k) => {
          if (k.includes(entryToken)) delete currentItem[k];
        });
        return {
          ...prev,
          [item.id]: currentItem
        };
      });
    };
    const getContainerRows = (container) => {
      if (Array.isArray(container?.containerRows) && container.containerRows.length > 0) return container.containerRows;
      if (Array.isArray(container?.nestedFields) && container.nestedFields.length > 0) return [{ id: `row_${container.id || 'legacy'}`, fields: container.nestedFields }];
      return [];
    };
    const getTableCellKey = (fieldKey, rowIndex, colIndex) => `${fieldKey}__cell_${rowIndex}_${colIndex}`;
    const getContainerStyle = (field) => {
      const rawBg = String(field?.backgroundColor || '').trim().toLowerCase();
      const hasBackgroundColor = Boolean(rawBg && rawBg !== 'transparent' && rawBg !== 'none');
      const style = {
        backgroundColor: hasBackgroundColor ? field.backgroundColor : 'transparent'
      };
      const borderColor = field.borderColor || 'var(--gray-400)';
      const sides = Array.isArray(field.borderSides) ? field.borderSides : [];
      if (sides.includes('all')) style.border = `1px solid ${borderColor}`;
      else {
        if (sides.includes('top')) style.borderTop = `1px solid ${borderColor}`;
        if (sides.includes('right')) style.borderRight = `1px solid ${borderColor}`;
        if (sides.includes('bottom')) style.borderBottom = `1px solid ${borderColor}`;
        if (sides.includes('left')) style.borderLeft = `1px solid ${borderColor}`;
      }
      return style;
    };

    const renderPreviewField = (f, parentContainer = null, keyPrefix = 'field', entryId = 'legacy') => {
      const baseFieldId = f?.id || f?.key || `${keyPrefix}_key`;
      const fieldId = resolveFieldKeyForEntry(baseFieldId, entryId);
      const value = values[fieldId] ?? (f.defaultValue ?? '');
      const type = f?.type || 'text';

      if (type === 'header') {
        const Tag = f.headingLevel || 'h3';
        const alignment = f.headerAlignment || 'left';
        const alignmentClass = alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-end' : 'text-start';
        return (
          <div className={alignmentClass}>
            <Tag className="mb-1">{f.label || ''}</Tag>
            {f.headerSubtitle ? <div className="text-muted fw-semibold mb-1">{f.headerSubtitle}</div> : null}
            {f.headerDescription ? <p className="text-muted small mb-2">{f.headerDescription}</p> : null}
          </div>
        );
      }
      if (type === 'header_logo_content') {
        const container = parentContainer || {};
        return (
          <div className={`d-flex flex-row gap-2 align-items-center ${container.alignment === 'center' ? 'justify-content-center' : container.alignment === 'right' ? 'justify-content-end' : 'justify-content-start'}`}>
            {container.logo ? <img src={container.logo} alt="Form logo" style={{ maxHeight: 48, objectFit: 'contain' }} /> : null}
            <div className="d-flex flex-column">
              {container.headerTitle ? <h5 className="mb-0 fw-semibold">{container.headerTitle}</h5> : null}
              {container.headerSubtitle ? <p className="text-muted mb-0">{container.headerSubtitle}</p> : null}
            </div>
          </div>
        );
      }
      if (type === 'textinfo') return <div className="text-muted mb-2" dangerouslySetInnerHTML={{ __html: f.textContent || '' }} />;
      if (type === 'divider') {
        const spacing = Number(f.dividerSpacing ?? 16);
        const dividerStyle = f.dividerStyle || 'solid';
        const dividerThickness = Number(f.dividerThickness || 1);
        const dividerColor = f.dividerColor || 'var(--gray-400)';
        return (
          <div style={{ paddingTop: spacing, paddingBottom: spacing }}>
            <hr className="m-0" style={{ borderTop: `${dividerThickness}px ${dividerStyle} ${dividerColor}` }} />
          </div>
        );
      }
      if (type === 'spacer') return <div style={{ height: Number(f.spacerHeight || 24) }} />;
      if (type === 'table' && Array.isArray(f.tableData)) {
        return (
          <div className="d-flex flex-column gap-1">
            {f.tableTitle ? <div className="fw-semibold">{f.tableTitle}</div> : null}
            {f.tableSubtitle ? <div className="text-muted fw-semibold">{f.tableSubtitle}</div> : null}
            {f.tableDescription ? <p className="text-muted small mb-1">{f.tableDescription}</p> : null}
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <tbody>
                  {f.tableData.map((row, r) => (
                    <tr key={`${fieldId}_row_${r}`}>
                      {row.map((cell, c) => {
                        const isHeaderOrFirstCol = r === 0 || c === 0;
                        const hasTemplateText = String(cell?.content || '').trim().length > 0;
                        const CellTag = r === 0 ? 'th' : 'td';
                        const cellKey = getTableCellKey(fieldId, r, c);
                        return (
                          <CellTag key={cell.id || `${fieldId}_cell_${r}_${c}`}>
                            {isHeaderOrFirstCol && hasTemplateText ? (
                              <div>{cell.content}</div>
                            ) : (
                              <Form.Control
                                value={values[cellKey] || ''}
                                disabled={readOnly}
                                onChange={(e) => {
                                  if (readOnly) return;
                                  const nextVal = e.target.value;
                                  setInfoFormValuesByItemId(prev => ({
                                    ...prev,
                                    [item.id]: {
                                      ...(prev[item.id] || {}),
                                      [cellKey]: nextVal
                                    }
                                  }));
                                }}
                                placeholder={r === 0 ? 'Header' : c === 0 ? 'Row label' : 'Value'}
                              />
                            )}
                          </CellTag>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      const label = f.label || f.title || fieldId;
      const required = Boolean(f.required);
      if (type === 'textarea') {
        return (
          <Form.Group>
            <Form.Label>{label} {required ? '*' : null}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={value}
              disabled={readOnly}
              onChange={(e) => {
                if (readOnly) return;
                const nextVal = e.target.value;
                setInfoFormValuesByItemId(prev => ({
                  ...prev,
                  [item.id]: { ...(prev[item.id] || {}), [fieldId]: nextVal }
                }));
              }}
            />
          </Form.Group>
        );
      }
      if (type === 'rich_textarea') {
        return (
          <Form.Group>
            <Form.Label>{label} {required ? '*' : null}</Form.Label>
            {readOnly ? (
              <div
                className="border rounded p-2 bg-light-subtle"
                dangerouslySetInnerHTML={{ __html: value || '<p class="text-muted mb-0">No response provided.</p>' }}
              />
            ) : (
              <RichTextEditor
                value={value || ''}
                onChange={(nextVal) => {
                  setInfoFormValuesByItemId(prev => ({
                    ...prev,
                    [item.id]: { ...(prev[item.id] || {}), [fieldId]: nextVal }
                  }));
                }}
                placeholder={f?.placeholder || 'Enter response...'}
              />
            )}
          </Form.Group>
        );
      }
      if (type === 'number') {
        return (
          <Form.Group>
            <Form.Label>{label} {required ? '*' : null}</Form.Label>
            <Form.Control
              type="number"
              value={value}
              disabled={readOnly}
              onChange={(e) => {
                if (readOnly) return;
                const nextVal = e.target.value ? Number(e.target.value) : '';
                setInfoFormValuesByItemId(prev => ({
                  ...prev,
                  [item.id]: { ...(prev[item.id] || {}), [fieldId]: nextVal }
                }));
              }}
            />
          </Form.Group>
        );
      }
      if (type === 'phone') {
        return (
          <Form.Group>
            <Form.Label>{label} {required ? '*' : null}</Form.Label>
            <Form.Control
              type="tel"
              value={formatPhoneValue(value)}
              disabled={readOnly}
              onChange={(e) => {
                if (readOnly) return;
                const nextVal = formatPhoneValue(e.target.value);
                setInfoFormValuesByItemId(prev => ({
                  ...prev,
                  [item.id]: { ...(prev[item.id] || {}), [fieldId]: nextVal }
                }));
              }}
              placeholder="(###) ###-####"
            />
          </Form.Group>
        );
      }
      if (type === 'ssn') {
        return (
          <Form.Group>
            <Form.Label>{label} {required ? '*' : null}</Form.Label>
            <Form.Control
              type={readOnly ? 'text' : 'password'}
              value={readOnly ? maskSsnValue(value) : formatSsnValue(value)}
              disabled={readOnly}
              onChange={(e) => {
                if (readOnly) return;
                const nextVal = formatSsnValue(e.target.value);
                setInfoFormValuesByItemId(prev => ({
                  ...prev,
                  [item.id]: { ...(prev[item.id] || {}), [fieldId]: nextVal }
                }));
              }}
              placeholder="###-##-####"
            />
          </Form.Group>
        );
      }
      if (type === 'select' || type === 'dropdown') {
        const opts = Array.isArray(f.options) ? f.options : [];
        const displayTitle = f.choiceTitle || f.label || fieldId;
        const displayDescription = f.choiceDescription;
        return (
          <Form.Group>
            {(displayTitle || required) ? (
              <Form.Label>
                {displayTitle || ''} {required ? '*' : null}
              </Form.Label>
            ) : null}
            {displayDescription ? <div className="text-muted small mb-1">{displayDescription}</div> : null}
            <Form.Select
              value={value}
              disabled={readOnly}
              onChange={(e) => {
                if (readOnly) return;
                const nextVal = e.target.value;
                setInfoFormValuesByItemId(prev => ({
                  ...prev,
                  [item.id]: { ...(prev[item.id] || {}), [fieldId]: nextVal }
                }));
              }}
            >
              <option value="">Select...</option>
              {opts.map((opt, idx) => {
                const v = typeof opt === 'string' ? opt : opt.value;
                const l = typeof opt === 'string' ? opt : opt.label || opt.value;
                return <option key={`${fieldId}-${idx}`} value={v}>{l}</option>;
              })}
            </Form.Select>
          </Form.Group>
        );
      }
      if (type === 'checkbox') {
        const displayTitle = f.choiceTitle || f.label || fieldId;
        const displayDescription = f.choiceDescription;
        return (
          <Form.Group>
            {(displayTitle || required) ? (
              <Form.Label>
                {displayTitle || ''} {required ? '*' : null}
              </Form.Label>
            ) : null}
            {displayDescription ? <div className="text-muted small mb-1">{displayDescription}</div> : null}
            <Form.Check
              type="checkbox"
              checked={Boolean(value)}
              disabled={readOnly}
              onChange={(e) => {
                if (readOnly) return;
                const nextVal = e.target.checked;
                setInfoFormValuesByItemId(prev => ({
                  ...prev,
                  [item.id]: { ...(prev[item.id] || {}), [fieldId]: nextVal }
                }));
              }}
            />
          </Form.Group>
        );
      }
      if (type === 'signature') {
        const signKey = `${item.id}:${fieldId}`;
        return (
          <Form.Group className="mb-2">
            <Form.Label className="fw-semibold">{label} {required ? '*' : null}</Form.Label>
            <SignatureField
              field={f}
              value={value}
              readOnly={readOnly}
              signing={signingSignatureKey === signKey}
              onDraftChange={(draft) => {
                if (readOnly) return;
                setInfoFormValuesByItemId((prev) => ({
                  ...prev,
                  [item.id]: { ...(prev[item.id] || {}), [fieldId]: draft }
                }));
              }}
              onSign={async (payload) => {
                if (readOnly || isAdminPreview) return;
                setSigningSignatureKey(signKey);
                try {
                  const res = await axios.post(
                    `${API_ENDPOINTS.ONBOARDING}/package-section-assignments/${sectionAssignmentId}/items/${item.id}/sign-field`,
                    { fieldId: f.id || fieldId, signerName: payload.signerName, consents: payload.consents },
                    { headers }
                  );
                  const nextFormData = res.data?.formData;
                  if (nextFormData && typeof nextFormData === 'object') {
                    setInfoFormValuesByItemId((prev) => ({
                      ...prev,
                      [item.id]: { ...(prev[item.id] || {}), ...nextFormData }
                    }));
                  } else if (res.data?.value) {
                    setInfoFormValuesByItemId((prev) => ({
                      ...prev,
                      [item.id]: { ...(prev[item.id] || {}), [fieldId]: res.data.value }
                    }));
                  }
                } finally {
                  setSigningSignatureKey(null);
                }
              }}
            />
          </Form.Group>
        );
      }
      if (type === 'yes_no' || type === 'true_false') {
        const positiveLabel = type === 'yes_no' ? 'Yes' : 'True';
        const negativeLabel = type === 'yes_no' ? 'No' : 'False';
        const isVerticalLayout = (f?.booleanLayout ?? 'horizontal') === 'vertical';
        return (
          <Form.Group className={isVerticalLayout ? 'd-flex flex-column align-items-start gap-2 mb-2' : 'd-flex align-items-center justify-content-between gap-2 mb-2'}>
            <Form.Label className="mb-0">{label} {required ? '*' : null}</Form.Label>
            <ButtonGroup size="sm">
              <Button
                type="button"
                variant={value === true ? 'primary' : 'outline-secondary'}
                disabled={readOnly}
                onClick={() => {
                  if (readOnly) return;
                  setInfoFormValuesByItemId(prev => ({
                    ...prev,
                    [item.id]: { ...(prev[item.id] || {}), [fieldId]: true }
                  }));
                }}
              >
                {positiveLabel}
              </Button>
              <Button
                type="button"
                variant={value === false ? 'primary' : 'outline-secondary'}
                disabled={readOnly}
                onClick={() => {
                  if (readOnly) return;
                  setInfoFormValuesByItemId(prev => ({
                    ...prev,
                    [item.id]: { ...(prev[item.id] || {}), [fieldId]: false }
                  }));
                }}
              >
                {negativeLabel}
              </Button>
            </ButtonGroup>
          </Form.Group>
        );
      }
      return (
        <Form.Group>
          <Form.Label>{label} {required ? '*' : null}</Form.Label>
          <Form.Control
            type="text"
            value={value}
            disabled={readOnly}
            onChange={(e) => {
              if (readOnly) return;
              const nextVal = e.target.value;
              setInfoFormValuesByItemId(prev => ({
                ...prev,
                [item.id]: { ...(prev[item.id] || {}), [fieldId]: nextVal }
              }));
            }}
          />
        </Form.Group>
      );
    };

    return (
      <Form>
        <div className="d-flex flex-column gap-3">
          {fields.map((f, idx) => (
            <div key={f?.id || `preview-field-${idx}`}>
              {!evaluateConditionalRule(f.conditionalRule) ? null : (!f?.isContainer ? (
                <div>{renderPreviewField(f, null, `top_${idx}`)}</div>
              ) : (
                <div
                  className={`rounded ${((f.backgroundColor && !['transparent', 'none'].includes(String(f.backgroundColor).toLowerCase())) || (Array.isArray(f.borderSides) && f.borderSides.length > 0)) ? 'p-3' : ''}`}
                  style={getContainerStyle(f)}
                >
                  {(f.sectionTitle || f.sectionSubtitle || f.sectionDescription) && (
                    <>
                      {f.sectionTitle && <h6 className="mb-1 fw-semibold">{f.sectionTitle}</h6>}
                      {f.sectionSubtitle && <div className="text-muted fw-semibold mb-1">{f.sectionSubtitle}</div>}
                      {f.sectionDescription && <p className="text-muted small mb-2">{f.sectionDescription}</p>}
                    </>
                  )}
                  <div className="d-flex flex-column gap-2">
                  {getContainerEntryIds(f).map((entryId, entryIdx) => (
                    <div key={`${f.id || `container_${idx}`}_entry_${entryId}`} className={entryIdx > 0 ? 'pt-2 mt-1 border-top' : ''}>
                      {f.allowMultipleEntries && (
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                          <div className="text-muted small">Entry {entryIdx + 1}</div>
                          {!readOnly && entryId !== 'legacy' && (
                            <Button size="sm" variant="outline-danger" onClick={() => removeContainerEntry(f, entryId)}>
                              Remove entry
                            </Button>
                          )}
                        </div>
                      )}
                      {getContainerRows(f).map((row, rowIdx) => {
                        if (!evaluateConditionalRule(row.conditionalRule, entryId)) return null;
                        const rowHasBooleanChoice = (row.fields || []).some((nf) => ['yes_no', 'true_false'].includes(nf?.type));
                        return (
                      <div key={`${row.id || `row_${rowIdx}`}_${entryId}`}>
                        <div className={`d-flex gap-2 ${rowHasBooleanChoice ? 'align-items-end' : ''}`}>
                          {(row.fields || []).map((nf, nfIdx) => (
                            !evaluateConditionalRule(nf.conditionalRule, entryId) ? null : (
                            <div key={`${nf.id || `nf_${nfIdx}`}_${entryId}`} style={{ flex: nf.flexBasis || 1 }}>
                              {renderPreviewField(nf, f, `nested_${idx}_${rowIdx}_${nfIdx}`, entryId)}
                            </div>
                            )
                          ))}
                        </div>
                      </div>
                    );
                      })}
                    </div>
                  ))}
                  {!readOnly && f.allowMultipleEntries && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => addContainerEntry(f)}
                      className="align-self-start mt-1"
                    >
                      Add another entry
                    </Button>
                  )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Form>
    );
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <Spinner animation="border" className="text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  const content = (
    <>
    <Card className={`border-0 ${embedded ? '' : 'shadow-sm'}`}>
      {embedded ? null : (
        <Row className="mb-3">
          <Col>
          <div className="d-flex flex-column align-items-start px-4 py-3 border-bottom">
            <h5 className="mb-0">Onboarding Completion</h5>
            <small className="text-muted">Complete items by section. Save progress as you go.</small>
            </div>
          </Col>
        </Row>
      )}

      {packagesLoading && !embedded && (
        <div className="mb-3">
          <Spinner animation="border" size="sm" /> <span className="text-muted small ms-2">Loading...</span>
        </div>
      )}

      {sectionAssignments.length === 0 ? (
        <Alert variant="info">No onboarding sections assigned.</Alert>
      ) : assignmentsForDisplay.length === 0 ? (
        hideApprovedSections ? null : (
          <Alert variant="info">No onboarding sections assigned.</Alert>
        )
      ) : (
        <>
          {embedded && packageAssignmentId ? (
            (() => {
              const group = groupedByPackageAssignment[0];
              if (!group) return <Alert variant="info">No onboarding sections assigned.</Alert>;

              return (
                <div>
                  {isAdminPreview && (
                    <Alert variant="light" className="border small py-2 mb-3">
                      <i className="bi bi-eye me-2"></i>
                      Preview — this matches the resident layout in My Assignments. Use the package content editor to change sections and items.
                    </Alert>
                  )}

                  <div className="d-flex flex-column gap-3">
                    {group.sectionAssignments.map((sa, index) => {
                      const sectionItems = sa?.section?.items || [];
                      const approved = sa.status === 'approved';
                      const canSubmit = canSubmitSection(sa);
                      const isSectionSubmitted = sa.status === 'submitted' || sa.status === 'approved' || locallySubmittedSections.has(sa.id);


                      

                      return (
                        <div key={sa.id}>
                            <div className="d-flex justify-content-between align-items-start gap-3">
                              <div className="d-flex flex-row w-100 justify-content-between align-items-center gap-2">
                                <div className="d-flex flex-row align-items-center gap-2">
                                <span className="bg-secondary rounded-3 px-3 py-2 fw-semibold">S{index + 1 ? `${index + 1} ` : ''}</span>
                                <div className="d-flex flex-column">
                                  <h6 className="fw-semibold mb-0">
                                    {sa.section?.title || 'Section'}
                                  </h6>
                                  <small className="text-muted">
                                    {sectionItems.length} item{sectionItems.length === 1 ? '' : 's'}
                                  </small>
                                </div></div>
                                  <div className="d-flex flex-column align-items-end" style={{ minWidth: 180 }}>
                                    {/*<Badge
                                      bg={approved ? 'success' : sa.status === 'rejected' ? 'danger' : sa.status === 'submitted' ? 'info' : 'warning'}
                                      className="rounded-pill px-3 py-2 mb-1"
                                    >
                                      {sectionReadyLabel}
                                    </Badge>*/}
                                    {/* Progress bar for section items */}
                                    {sectionItems.length > 0 && (() => {
                                      const completedItems = sectionItems.filter(item => itemIsCompleteByUser(sa, item)).length;
                                      const percent = Math.round((completedItems / sectionItems.length) * 100);
                                      return (
                                        <div style={{ width: 110 }}>
                                          <div className="progress" style={{ height: 8 }}>
                                            <div
                                              className={`progress-bar${percent === 100 ? ' bg-success-solid' : percent === 0 ? ' bg-secondary' : ' bg-info'}`}
                                              role="progressbar"
                                              style={{ width: `${percent}%` }}
                                              aria-valuenow={percent}
                                              aria-valuemin="0"
                                              aria-valuemax="100"
                                            />
                                          </div>
                                          <div className="text-muted text-end small mt-1">
                                            <span className="fw-semibold">{completedItems}</span><span> of {sectionItems.length} complete</span>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                              </div>
                                <Button
                                  size="sm"
                                  variant={isSectionSubmitted ? 'success' : (canSubmit ? 'primary' : 'secondary')}
                                  disabled={isSectionSubmitted || !canSubmit || isAdminPreview}
                                  className=" text-nowrap"
                                  onClick={() => submitSection(sa.id)}
                                >
                                  <i className="bi bi-send me-1"></i>
                                  {isSectionSubmitted ? 'Submitted' : 'Submit section'}
                                </Button>
                            </div>

                            {sa.section?.instructions && (
                              <p className="text-muted mb-0 mt-2" style={{ whiteSpace: 'pre-wrap' }}>{sa.section.instructions}</p>
                            )}

                            <div className="mt-3 d-flex flex-column gap-2">
                              {sectionItems.some((it) => it.itemType !== 'info_form') && (
                                <div className="small text-muted fw-semibold">Documents & document requests</div>
                              )}
                              {sectionItems.map((item) => {
                                const resp = getItemResponse(sa, item.id);
                                const status = resp?.status || 'draft';
                                const userDone = itemIsCompleteByUser(sa, item);
                                const disabledByLock = approved || sa.status === 'submitted' || sa.status === 'approved';

                                const fileAvailable = Boolean(resp?.filePath || resp?.fileName || resp?.webPath);
                                const fileSchema = item.formSchema || {};
                                const isReferenceFilesMode = item.itemType === 'file_upload' && fileSchema.contentMode === 'reference_files' && Array.isArray(fileSchema.referenceFiles);
                                const acknowledged = resp?.formData?.acknowledged || {};
                                const uploadedFor = resp?.formData?.uploadedFor || {};

                                const index = sectionItems.findIndex((si) => si.id === item.id);
                                const displayIndex = index >= 0 ? index + 1 : 0;
                                if (item.itemType === 'info_form') return null;

                                return (
                                  <div key={item.id} className="p-3 rounded-3 border">
                                    <div className="d-flex justify-content-between align-items-center gap-3">
                                      <div className="d-flex flex-column">
                                        <span className="fw-semibold"> {displayIndex ? `${displayIndex}. ` : ''}{item.title}</span>
                                        {item.instructions && (
                                          <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                                            {item.instructions}
                                          </div>
                                        )}</div>
                                        
                                          <Badge
                                            bg={status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : status === 'submitted' ? 'info' : userDone ? 'info' : 'secondary'}
                                            className="rounded-pill"
                                          >
                                            {status === 'approved'
                                              ? 'Approved'
                                              : status === 'rejected'
                                                ? 'Rejected'
                                                : status === 'submitted'
                                                  ? 'Completed'
                                                  : userDone
                                                    ? 'Saved'
                                                    : 'Incomplete'}
                                          </Badge>

                                         
                                    </div>

                                        {item.itemType === 'file_upload' && (
                                          <div className="mt-2">
                                            {isReferenceFilesMode ? (
                                              <div className="d-flex flex-column gap-2">
                                                {fileSchema.referenceFiles.map((rf) => {
                                                  const completed = rf.completionMode === 'upload_required'
                                                    ? Boolean(uploadedFor[rf.id])
                                                    : Boolean(acknowledged[rf.id]);
                                                  const uploadedEntry = uploadedFor[rf.id];
                                                  return (
                                                    <div key={rf.id} className="p-2 ps-3 rounded-3 bg-light d-flex flex-row justify-content-between align-items-center gap-2">
                                                        <div className="text-truncate">
                                                          <Button
                                                            type="button"
                                                            variant="link"
                                                            className="p-0 text-decoration-none fw-semibold text-truncate"
                                                            title={rf.originalName || rf.fileName || 'Open file'}
                                                            onClick={() => openSecureOnboardingFileInNewTab(rf.webPath)}
                                                          >
                                                            {rf.originalName || rf.fileName}
                                                          </Button>
                                                        </div>
                                                      <div>
                                                        {rf.completionMode === 'upload_required' ? (
                                                          <div className="d-flex align-items-center justify-content-between gap-2">
                                                            <div className="text-muted small">
                                                              {uploadedEntry?.originalName ? (
                                                                <>Uploaded: <span className="fw-semibold small">{uploadedEntry.originalName}</span></>
                                                              ) : (
                                                                <>No file uploaded yet.</>
                                                              )}
                                                            </div>
                                                            <div className="d-flex gap-2">
                                                              {uploadedEntry?.originalName && (
                                                                <Button
                                                                  size="sm"
                                                                  variant="outline-danger"
                                                                  disabled={disabledByLock || status === 'approved'}
                                                                  onClick={() => clearUploadedEvidence(sa.id, item.id, rf.id)}
                                                                >
                                                                  <i className="bi bi-trash me-1"></i>
                                                                  Remove
                                                                </Button>
                                                              )}
                                                              <Button
                                                                size="sm"
                                                                variant="outline-primary"
                                                                style={{ display: status === 'approved' ? 'none' : undefined }}
                                                                disabled={isAdminPreview || disabledByLock}
                                                                onClick={() => {
                                                                  setUploadTarget({
                                                                    sectionAssignmentId: sa.id,
                                                                    itemId: item.id,
                                                                    requiredFileTypes: item.requiredFileTypes,
                                                                    referenceFileId: rf.id
                                                                  });
                                                                  setShowUploadModal(true);
                                                                }}
                                                              >
                                                                <i className="bi bi-upload me-1"></i>
                                                                {uploadedEntry?.originalName ? 'Upload new' : 'Upload evidence'}
                                                              </Button>
                                                            </div>
                                                          </div>
                                                        ) : (
                                                          <Button
                                                            variant={acknowledged[rf.id] ? "light-solid" : "outline-primary"}
                                                            size="sm"
                                                            className="d-flex align-items-center px-3"
                                                            disabled={disabledByLock || status === 'approved' || isAdminPreview}
                                                            onClick={() =>
                                                              saveReferenceAcknowledgement(
                                                                sa.id,
                                                                item.id,
                                                                rf.id,
                                                                !acknowledged[rf.id]
                                                              )
                                                            }
                                                            style={{ userSelect: "none" }}
                                                          >
                                                            <Form.Check
                                                              type="checkbox"
                                                              className="me-2"
                                                              style={{ pointerEvents: "none" }}
                                                              checked={Boolean(acknowledged[rf.id])}
                                                              readOnly
                                                            />
                                                            <span>I acknowledge that I have read this file</span>
                                                          </Button>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              <>
                                                {fileAvailable && (
                                                  <div className="d-flex flex-column gap-2 mb-2">
                                                    
                                                      <span className="text-muted small fw-semibold">Files Uploaded:</span> 
                                                      <div className=" bg-light p-2 ps-3 d-flex flex-row justify-content-between align-items-center gap-2 rounded-3">
                                                        <span className=" fw-semibold">{resp?.originalName || resp?.fileName}</span>
                                                   
                                                    <Button
                                                      size="sm"
                                                      variant="outline-danger"
                                                      className="px-2 py-1"
                                                      disabled={disabledByLock || status === 'approved' || isAdminPreview}
                                                      onClick={() => clearUploadedEvidence(sa.id, item.id, null)}
                                                    >
                                                      <i className="bi bi-trash "></i>
                                                    </Button></div>
                                                  </div>
                                                )}
                                                <Button
                                                  size="sm"
                                                  variant="outline-primary"
                                                  style={{ display: status === 'approved' ? 'none' : undefined }}
                                                  disabled={isAdminPreview || disabledByLock}
                                                  onClick={() => {
                                                    setUploadTarget({
                                                      sectionAssignmentId: sa.id,
                                                      itemId: item.id,
                                                      requiredFileTypes: item.requiredFileTypes
                                                    });
                                                    setShowUploadModal(true);
                                                  }}
                                                >
                                                  <i className="bi bi-upload me-1"></i>
                                                  {fileAvailable ? 'Upload new' : 'Upload file'}
                                                </Button>
                                              </>
                                            )}
                                          </div>
                                        )}

                                        {item.itemType === 'info_form' && (
                                          <div className="mt-2 p-2 rounded-3 bg-light d-flex justify-content-between align-items-start gap-2">
                                            <div>
                                              <div className="fw-semibold">{item.title || 'Form'}</div>
                                              <div className="text-muted small">{item.instructions || 'Complete this form in the form module.'}</div>
                                              {disabledByLock ? (
                                                <Alert variant="secondary" className="mb-0 mt-2 py-2">This item is locked.</Alert>
                                              ) : null}
                                            </div>
                                            <Button
                                              size="sm"
                                              variant="primary"
                                              disabled={isAdminPreview}
                                              onClick={() => {
                                                const respForItem = getItemResponse(sa, item.id);
                                                setActiveInfoFormTarget({
                                                  sectionAssignmentId: sa.id,
                                                  item,
                                                  readOnly: disabledByLock || status === 'approved' || isAdminPreview,
                                                  reviewNotes: status === 'rejected' ? (respForItem?.reviewNotes || '') : ''
                                                });
                                              }}
                                            >
                                              {(disabledByLock || status === 'approved') ? 'View form' : 'Open form'}
                                            </Button>
                                          </div>
                                        )}
                                     
                                  </div>
                                );
                              })}
                              {(() => {
                                const formItems = sectionItems.filter((it) => it.itemType === 'info_form');
                                if (!formItems.length) return null;
                                return (
                                  <Accordion className="mt-2" defaultActiveKey={['forms']}>
                                    <Accordion.Item eventKey="forms" className="border rounded-3">
                                      <Accordion.Header>
                                        <span className="fw-semibold">Forms</span>
                                      </Accordion.Header>
                                      <Accordion.Body className="d-flex flex-column gap-2">
                                        {formItems.map((item, idx) => {
                                          const respForItem = getItemResponse(sa, item.id);
                                          const status = respForItem?.status || 'draft';
                                          const disabledByLock = approved || sa.status === 'submitted' || sa.status === 'approved';
                                          return (
                                            <div key={`form-only-${item.id}`} className="p-2 rounded-3 bg-light d-flex justify-content-between align-items-start gap-2">
                                              <div>
                                                <div className="fw-semibold">{idx + 1}. {item.title || 'Form'}</div>
                                                <div className="text-muted small">{item.instructions || 'Complete this form in the form module.'}</div>
                                                {disabledByLock ? (
                                                  <Alert variant="secondary" className="mb-0 mt-2 py-2">This item is locked.</Alert>
                                                ) : null}
                                              </div>
                                              <Button
                                                size="sm"
                                                variant="primary"
                                                disabled={isAdminPreview}
                                                onClick={() => {
                                                  setActiveInfoFormTarget({
                                                    sectionAssignmentId: sa.id,
                                                    item,
                                                    readOnly: disabledByLock || status === 'approved' || isAdminPreview,
                                                    reviewNotes: status === 'rejected' ? (respForItem?.reviewNotes || '') : ''
                                                  });
                                                }}
                                              >
                                                {(disabledByLock || status === 'approved') ? 'View form' : 'Open form'}
                                              </Button>
                                            </div>
                                          );
                                        })}
                                      </Accordion.Body>
                                    </Accordion.Item>
                                  </Accordion>
                                );
                              })()}
                            </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()
          ) : (
            <Accordion className="px-2"
              defaultActiveKey={
                groupedByPackageAssignment[0]?.packageAssignment?.id
                  ? String(groupedByPackageAssignment[0].packageAssignment.id)
                  : undefined
              }
            >
              {groupedByPackageAssignment.map((group) => {
                const pkgAssign = group.packageAssignment;
                const allApproved = group.sectionAssignments.every(sa => sa.status === 'approved');
                const hasRejected = group.sectionAssignments.some(sa => sa.status === 'rejected');
                const headerBadgeVariant = allApproved ? 'success' : hasRejected ? 'danger' : 'warning';
                const headerBadgeLabel = allApproved ? 'All approved' : hasRejected ? 'Needs work' : 'In progress';

                return (
                  <Accordion.Item
                    key={pkgAssign.id}
                    eventKey={String(pkgAssign.id)}
                    className="border-0 mb-2"
                  >
                    <Accordion.Header className="p-0">
             
                         <div className="d-flex flex-row align-items-center justify-content-between gap-2 w-100">
                          <div className="d-flex flex-column">
                            <div className="fw-semibold">
                              {pkgAssign.customName || pkgAssign.package?.name || 'Package'}
                            </div>
                            <div className="text-muted small">
                              {group.sectionAssignments.length} section(s)
                            </div>
                          </div>
                          <Badge bg={headerBadgeVariant} className="me-3">
                            {headerBadgeLabel}
                          </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="pt-0">
                      <div className="mb-3">
                        {pkgAssign.package?.description ? (
                          <div className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{pkgAssign.package.description}</div>
                        ) : (
                          <div className="text-muted small">No package description.</div>
                        )}
                      </div>

                      <div className="d-flex flex-column gap-3">
                        {group.sectionAssignments.map((sa, index) => {
                          const sectionItems = sa?.section?.items || [];
                          const approved = sa.status === 'approved';
                          const canSubmit = canSubmitSection(sa);
                          const isSectionSubmitted = sa.status === 'submitted' || sa.status === 'approved' || locallySubmittedSections.has(sa.id);

                          return (
                            <div key={sa.id}>
                                <div className="d-flex justify-content-between align-items-start gap-3">
                                  <div className="d-flex justify-content-between align-items-center gap-2 w-100">
                                    <div className="d-flex flex-row align-items-center gap-2">
                                      <span className="bg-secondary rounded-3 px-3 py-2 fw-semibold">S{index + 1 ? `${index + 1} ` : ''}</span>
                                    <div className="d-flex flex-column">
                                      <h6 className="fw-semibold mb-0">
                                        {sa.section?.title || 'Section'}
                                        </h6>
                                        <small className="text-muted">
                                          {sectionItems.length} {sectionItems.length === 1 ? 'item' : 'items'}
                                        </small> 
                                      </div>
                                    </div>
                                      {/*<Badge
                                        bg={approved ? 'success' : sa.status === 'rejected' ? 'danger' : sa.status === 'submitted' ? 'info' : 'warning'}
                                        className="rounded-pill px-3 py-2"
                                      >
                                        {sectionReadyLabel}
                                      </Badge>*/}
                                      {/* Progress bar for section items */}
                                      {sectionItems.length > 0 && (() => {
                                        const completedItems = sectionItems.filter(item => itemIsCompleteByUser(sa, item)).length;
                                        const percent = Math.round((completedItems / sectionItems.length) * 100);
                                        return (
                                          <div style={{ width: 110 }}>
                                            <div className="progress" style={{ height: 8 }}>
                                              <div
                                                className={`progress-bar${percent === 100 ? ' bg-success-solid' : percent === 0 ? ' bg-secondary' : ' bg-success-solid'}`}
                                                role="progressbar"
                                                style={{ width: `${percent}%` }}
                                                aria-valuenow={percent}
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                              />
                                            </div>
                                            <div className="text-muted text-end small mt-1">
                                              <span className="fw-semibold">{completedItems}</span><span> of {sectionItems.length} complete</span>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                  </div>
                                    <Button
                                      size="sm"
                                      variant={isSectionSubmitted ? 'success' : (canSubmit ? 'primary' : 'secondary')}
                                      disabled={isSectionSubmitted || !canSubmit}
                                      className="text-nowrap"
                                      onClick={() => submitSection(sa.id)}
                                    >
                                      <i className="bi bi-send me-1"></i>
                                      {isSectionSubmitted ? 'Submitted' : 'Submit section'}
                                    </Button>
                                </div>

                                <div className="mt-3 d-flex flex-column gap-2">
                                  <div> 
                                    <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                                        {sa.section.instructions}
                                      </p>
                                  </div>
                                  {sectionItems.some((it) => it.itemType !== 'info_form') && (
                                    <div className="small text-muted fw-semibold">Documents & document requests</div>
                                  )}
                                  {sectionItems.map((item) => {
                                    const resp = getItemResponse(sa, item.id);
                                    const status = resp?.status || 'draft';
                                    const userDone = itemIsCompleteByUser(sa, item);
                                    const disabledByLock = approved || sa.status === 'submitted' || sa.status === 'approved';

                                    const fileAvailable = Boolean(resp?.filePath || resp?.fileName || resp?.webPath);
                                    const fileSchema = item.formSchema || {};
                                    const isReferenceFilesMode = item.itemType === 'file_upload' && fileSchema.contentMode === 'reference_files' && Array.isArray(fileSchema.referenceFiles);
                                    const acknowledged = resp?.formData?.acknowledged || {};
                                    const uploadedFor = resp?.formData?.uploadedFor || {};

                                    const index = sectionItems.findIndex((si) => si.id === item.id);
                                    const displayIndex = index >= 0 ? index + 1 : 0;
                                    if (item.itemType === 'info_form') return null;

                                    return (
                                      <div key={item.id} className="p-3 rounded-3 border">
                                        <div className="d-flex flex-column gap-3">
                                          <div className="d-flex flex-row align-items-center gap-2">
                                          <div className="d-flex flex-column flex-grow-1">
                                            <div className="fw-semibold">{displayIndex ? `${displayIndex}. ` : ''}{item.title}</div>
                                            {item.instructions && (
                                              <div className="text-muted mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                                                {item.instructions}
                                              </div>
                                            )}
                                          </div>
                                          <div className="d-flex flex-column ">
                                              <Badge
                                                bg={status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : status === 'submitted' ? 'info' : userDone ? 'info' : 'secondary'}
                                                className="rounded-pill"
                                              >
                                                {status === 'approved'
                                                  ? 'Approved'
                                                  : status === 'rejected'
                                                    ? 'Rejected'
                                                    : status === 'submitted'
                                                      ? 'Completed'
                                                      : userDone
                                                        ? 'Saved'
                                                        : 'Incomplete'}
                                              </Badge>
                                            </div>
                                          </div>

                                          <div className="d-flex flex-column w-100">
                                            

                                            {item.itemType === 'file_upload' && (
                                              <div className="mt-2">
                                                {isReferenceFilesMode ? (
                                                  <div className="d-flex flex-column gap-2">
                                                  {fileSchema.referenceFiles.map((rf) => {
                                                      const completed = rf.completionMode === 'upload_required'
                                                        ? Boolean(uploadedFor[rf.id])
                                                        : Boolean(acknowledged[rf.id]);
                                                    const uploadedEntry = uploadedFor[rf.id];
                                                      return (
                                                        <div key={rf.id} className="p-2 ps-3 rounded-3 bg-light">
                                                          <div className="d-flex justify-content-between align-items-center gap-2">
                                                            <div className="text-truncate fw-semibold">
                                                              <Button
                                                                type="button"
                                                                variant="link"
                                                                className="p-0 text-decoration-none text-truncate"
                                                                title={rf.originalName || rf.fileName || 'Open file'}
                                                                onClick={() => openSecureOnboardingFileInNewTab(rf.webPath)}
                                                              >
                                                                {rf.originalName || rf.fileName}
                                                              </Button>
                                                            </div>

                                                          
                                                          <div>
                                                            {rf.completionMode === 'upload_required' ? (
                                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                              <div className="text-muted small">
                                                                {uploadedEntry?.originalName ? (
                                                                  <>
                                                                  <div className="d-flex flex-column w-100"></div>
                                                                  <span className="text-muted fw-semibold small">Files Uploaded:</span>
                                                                  <div className="fw-semibold bg-light p-2 rounded-3">{uploadedEntry.originalName}</div>
                                                                  
                                                                  </>
                                                                ) : (
                                                                  <>No file uploaded yet.</>
                                                                )}
                                                              </div>
                                                              <div className="d-flex gap-2">
                                                                {uploadedEntry?.originalName && (
                                                                  <Button
                                                                    size="sm"
                                                                    variant="outline-danger"
                                                                    disabled={disabledByLock || status === 'approved'}
                                                                    onClick={() => clearUploadedEvidence(sa.id, item.id, rf.id)}
                                                                  >
                                                                    <i className="bi bi-trash"></i>
                                                                  </Button>
                                                                )}
                                                                <Button
                                                                  size="sm"
                                                                  variant="outline-primary"
                                                                  disabled={disabledByLock || status === 'approved'}
                                                                  onClick={() => {
                                                                    setUploadTarget({
                                                                      sectionAssignmentId: sa.id,
                                                                      itemId: item.id,
                                                                      requiredFileTypes: item.requiredFileTypes,
                                                                      referenceFileId: rf.id
                                                                    });
                                                                    setShowUploadModal(true);
                                                                  }}
                                                                >
                                                                  <i className="bi bi-upload me-1"></i>
                                                                  {uploadedEntry?.originalName ? 'Upload new' : 'Upload evidence'}
                                                                </Button>
                                                              </div>
                                                            </div>
                                                            ) : (
                                                              <Button
                                                                variant={acknowledged[rf.id] ? "light-solid" : "outline-primary"}
                                                                size="sm"
                                                                className="d-flex align-items-center"
                                                                disabled={disabledByLock || status === 'approved'}
                                                                onClick={() => saveReferenceAcknowledgement(sa.id, item.id, rf.id, !acknowledged[rf.id])}
                                                              >
                                                                <span className="me-2">
                                                                  <input
                                                                    type="checkbox"
                                                                    checked={Boolean(acknowledged[rf.id])}
                                                                    readOnly
                                                                    className="form-check-input"
                                                                    style={{ pointerEvents: "none" }}
                                                                  />
                                                                </span>
                                                                I acknowledge that I have read this file
                                                              </Button>
                                                            )}
                                                            </div>
                                                          </div>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                ) : (
                                                  <>
                                                    {fileAvailable && (
                                                      
                                                        <div className="d-flex flex-column mb-2">
                                                          <span className="text-muted fw-semibold small mb-2">Files Uploaded:</span>
                                                          <div className="d-flex flex-row align-items-center justify-content-between bg-light p-2 ps-3 rounded-3 gap-2">
                                                            <span className="fw-semibold">{resp?.originalName || resp?.fileName}</span>
                                                              <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                disabled={disabledByLock || status === 'approved'}
                                                                className="px-2 py-1"
                                                                onClick={() => clearUploadedEvidence(sa.id, item.id, null)}
                                                              >
                                                                <i className="bi bi-trash"></i>
                                                              </Button>
                                                          </div> 
                                                        </div>
                                                      
                                                    )}
                                                    <Button
                                                      size="sm"
                                                      variant="outline-primary"
                                                      disabled={disabledByLock || status === 'approved'}
                                                      onClick={() => {
                                                        setUploadTarget({
                                                          sectionAssignmentId: sa.id,
                                                          itemId: item.id,
                                                          requiredFileTypes: item.requiredFileTypes
                                                        });
                                                        setShowUploadModal(true);
                                                      }}
                                                    >
                                                      <i className="bi bi-upload me-1"></i>
                                                      {fileAvailable ? 'Upload new' : 'Upload file'}
                                                    </Button>
                                                  </>
                                                )}
                                              </div>
                                            )}

                                            {item.itemType === 'info_form' && (
                                              <div className="mt-2 p-2 rounded-3 bg-light d-flex justify-content-between align-items-start gap-2">
                                                <div>
                                                  <div className="fw-semibold">{item.title || 'Form'}</div>
                                                  <div className="text-muted small">{item.instructions || 'Complete this form in the form module.'}</div>
                                                  {disabledByLock ? (
                                                    <Alert variant="secondary" className="mb-0 mt-2 py-2">This item is locked.</Alert>
                                                  ) : null}
                                                </div>
                                                <Button
                                                  size="sm"
                                                  variant="primary"
                                                  disabled={isAdminPreview}
                                                  onClick={() => {
                                                    const respForItem = getItemResponse(sa, item.id);
                                                    setActiveInfoFormTarget({
                                                      sectionAssignmentId: sa.id,
                                                      item,
                                                      readOnly: disabledByLock || status === 'approved' || isAdminPreview,
                                                      reviewNotes: status === 'rejected' ? (respForItem?.reviewNotes || '') : ''
                                                    });
                                                  }}
                                                >
                                                  {(disabledByLock || status === 'approved') ? 'View form' : 'Open form'}
                                                </Button>
                                              </div>
                                            )}
                                            </div>
                                          
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {(() => {
                                    const formItems = sectionItems.filter((it) => it.itemType === 'info_form');
                                    if (!formItems.length) return null;
                                    return (
                                      <Accordion className="mt-2" defaultActiveKey={['forms']}>
                                        <Accordion.Item eventKey="forms" className="border rounded-3">
                                          <Accordion.Header>
                                            <span className="fw-semibold">Forms</span>
                                          </Accordion.Header>
                                          <Accordion.Body className="d-flex flex-column gap-2">
                                            {formItems.map((item, idx) => {
                                              const respForItem = getItemResponse(sa, item.id);
                                              const status = respForItem?.status || 'draft';
                                              const disabledByLock = approved || sa.status === 'submitted' || sa.status === 'approved';
                                              return (
                                                <div key={`form-only-${item.id}`} className="p-2 rounded-3 bg-light d-flex justify-content-between align-items-start gap-2">
                                                  <div>
                                                    <div className="fw-semibold">{idx + 1}. {item.title || 'Form'}</div>
                                                    <div className="text-muted small">{item.instructions || 'Complete this form in the form module.'}</div>
                                                    {disabledByLock ? (
                                                      <Alert variant="secondary" className="mb-0 mt-2 py-2">This item is locked.</Alert>
                                                    ) : null}
                                                  </div>
                                                  <Button
                                                    size="sm"
                                                    variant="primary"
                                                    disabled={isAdminPreview}
                                                    onClick={() => {
                                                      setActiveInfoFormTarget({
                                                        sectionAssignmentId: sa.id,
                                                        item,
                                                        readOnly: disabledByLock || status === 'approved' || isAdminPreview,
                                                        reviewNotes: status === 'rejected' ? (respForItem?.reviewNotes || '') : ''
                                                      });
                                                    }}
                                                  >
                                                    {(disabledByLock || status === 'approved') ? 'View form' : 'Open form'}
                                                  </Button>
                                                </div>
                                              );
                                            })}
                                          </Accordion.Body>
                                        </Accordion.Item>
                                      </Accordion>
                                    );
                                  })()}
                                </div>
                              </div>
                          );
                        })}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}

          {showContinueToApp && (
            <div className="d-flex justify-content-end p-4 pt-2 ">
              <Button
                variant={isAllSectionsApproved ? 'primary' : 'secondary'}
                disabled={!isAllSectionsApproved}
                onClick={handleContinueToApp}
              >
                Continue to Application
              </Button>
            </div>
          )}
        </>
      )}

      <OnboardingUploadModal
        show={showUploadModal}
        onHide={() => {
          setShowUploadModal(false);
          setUploadTarget(null);
        }}
        onUpload={handleItemUpload}
        uploading={uploading}
        isAssignmentUpload={true}
      />
      <OnboardingFormModal
        show={Boolean(activeInfoFormTarget)}
        onHide={() => setActiveInfoFormTarget(null)}
        form={{
          title: activeInfoFormTarget?.item?.title || 'Form',
          description: activeInfoFormTarget?.item?.instructions || '',
          schema: activeInfoFormTarget?.item?.formSchema || {}
        }}
        assignment={{
          customName: activeInfoFormTarget?.item?.title || 'Form'
        }}
        initialData={infoFormValuesByItemId[activeInfoFormTarget?.item?.id] || {}}
        readOnly={Boolean(activeInfoFormTarget?.readOnly)}
        reviewNotes={activeInfoFormTarget?.reviewNotes || ''}
        signContext={
          activeInfoFormTarget
            ? {
                mode: 'package',
                sectionAssignmentId: activeInfoFormTarget.sectionAssignmentId,
                itemId: activeInfoFormTarget.item.id
              }
            : null
        }
        onSubmit={async (formData) => {
          if (!activeInfoFormTarget) return;
          await saveInfoFormItemData(
            activeInfoFormTarget.sectionAssignmentId,
            activeInfoFormTarget.item.id,
            formData
          );
        }}
      />
      </Card>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div>
      {!hideNavbar && (
        
              <Navbar bg="dark" variant="dark" expand="lg" className="mb-0">
                <Container className="d-flex flex-row align-items-center justify-content-between">
                  <Navbar.Brand>
                    <img src="/images/asset4.svg" alt="brand logo" style={{ height: "16px", marginTop: "-8px" }} />
                    {/*<img src="/images/logo4.svg" alt="brand logo" style={{ width: "32px", height: "32px", marginTop: "-4px"}}/>
                    <span className="ms-2">medhive</span>*/}
                  </Navbar.Brand>
                  <div className="d-flex flex-row align-items-center justify-content-end w-50 w-md-auto">
                    <ProgressBar
                      now={progressPercentage}
                      variant={progressPercentage === 100 ? "success-solid" : "primary-solid"}
                      className="mb-0"
                    />
                    <div className="d-flex flex-row gap-2 align-items-end text-white">
                      <div className="h6 mb-0 ms-2">{progressPercentage}%</div>
                      <small className="opacity-75 d-none d-md-block " >Complete</small>
                    </div>
                  </div>
                  <Button variant="light-lite" size="sm">
                    <i className="bi bi-sun-fill"></i>
                  </Button>
                  <Button
                    variant="light-lite"
                    size="sm"
                    onClick={() => {
                      localStorage.removeItem("token");
                      localStorage.removeItem("firstName");
                      localStorage.removeItem("lastName");
                      localStorage.removeItem("profilePic");
                      localStorage.removeItem("role");
                      localStorage.removeItem("documentRequirementsCompleted");
                      localStorage.removeItem("notifications");
                      localStorage.removeItem("lastNotificationTime");
                      window.dispatchEvent(new CustomEvent('app-logout'));
                      window.location.href = "/";
                    }}
                    
                  >
                    <i className="bi bi-box-arrow-right custom-btn"></i>
                  </Button>
                </Container>
              </Navbar>
      )}
      <Container className="pt-4 pb-5">
        {content}
      </Container>
    </div>
  );
};

export default DocumentCompletionPageV2;

